import { Check, ChevronRight, Clock3, CreditCard, Gift, Layers3, X } from 'lucide-react'
import type { PublicPlanView } from '@/utils/propostaPublicScope'
import { formatCurrency } from '@/utils/format-currency'
import { closedScopeTipoLabel } from './financeClosedScope'
import { cn } from '@/utils/cn'

type Props = {
  plan: PublicPlanView
  canViewFinancialValues: boolean
  onClose: () => void
}

export function ClosedScopeItemDetailModal({ plan, canViewFinancialValues, onClose }: Props) {
  return (
    <div
      className='fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4'
      onClick={onClose}
      role='presentation'
    >
      <div
        className='glass-card flex max-h-[min(90vh,880px)] w-full max-w-3xl flex-col overflow-hidden sm:rounded-2xl'
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-labelledby='closed-scope-detail-title'
      >
        <div className='flex shrink-0 items-start justify-between gap-3 border-b border-white/[0.06] px-5 py-4'>
          <div className='min-w-0'>
            <span
              className='inline-block rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide'
              style={{ color: plan.tipoColor, backgroundColor: `${plan.tipoColor}20` }}
            >
              {closedScopeTipoLabel(plan.item.tipo)}
            </span>
            <h2 id='closed-scope-detail-title' className='mt-2 text-lg font-bold text-text-primary'>
              {plan.title}
            </h2>
            {plan.subtitle && (
              <p className='mt-1 text-sm leading-relaxed text-text-muted'>{plan.subtitle}</p>
            )}
          </div>
          <button
            type='button'
            onClick={onClose}
            className='rounded-lg p-2 text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary'
            aria-label='Fechar'
          >
            <X size={18} />
          </button>
        </div>

        <div className='min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4'>
          {canViewFinancialValues && (
            <div className='grid gap-3 sm:grid-cols-2'>
              <div className='rounded-xl border border-white/[0.06] bg-white/[0.03] p-3'>
                <p className='text-[10px] font-bold uppercase tracking-wider text-text-muted'>
                  Investimento
                </p>
                <p className='mt-1 text-lg font-bold tabular-nums text-accent'>{plan.investmentLabel}</p>
                {plan.item.quantidade > 1 && (
                  <p className='text-xs text-text-muted'>
                    {formatCurrency(plan.item.valorUnitario)} / un. · Qtd. {plan.item.quantidade}
                  </p>
                )}
              </div>
              <div className='flex items-start gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3'>
                <CreditCard size={15} className='mt-0.5 shrink-0 text-accent' />
                <div>
                  <p className='text-[10px] font-bold uppercase tracking-wider text-text-muted'>
                    Pagamento
                  </p>
                  <p className='mt-0.5 text-xs text-text-primary'>{plan.paymentDetails}</p>
                </div>
              </div>
            </div>
          )}

          <div className='flex items-start gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3'>
            <Clock3 size={15} className='mt-0.5 shrink-0 text-emerald-400' />
            <div>
              <p className='text-[10px] font-bold uppercase tracking-wider text-text-muted'>Vigência</p>
              <p className='mt-0.5 text-xs text-text-primary'>{plan.loyaltyLabel}</p>
            </div>
          </div>

          {plan.bonus && (
            <div className='flex gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-4'>
              <Gift size={18} className='mt-0.5 shrink-0 text-emerald-400' />
              <div>
                <p className='text-[10px] font-bold uppercase tracking-wider text-emerald-400'>Bônus</p>
                <p className='mt-1 whitespace-pre-line text-sm text-text-primary'>{plan.bonus}</p>
              </div>
            </div>
          )}

          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <Layers3 size={15} className='text-accent' />
              <h3 className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>
                Escopo detalhado
              </h3>
              <span className='text-[10px] text-text-muted'>
                {plan.scopeCount} {plan.scopeCount === 1 ? 'entregável' : 'entregáveis'}
              </span>
            </div>

            {(plan.categories ?? []).length === 0 ? (
              <p className='rounded-xl border border-dashed border-white/[0.1] p-4 text-sm text-text-muted'>
                Nenhum entregável detalhado neste item. Consulte o closer ou o time financeiro.
              </p>
            ) : (
              <div className='grid gap-3 sm:grid-cols-2'>
                {(plan.categories ?? []).map((category) => (
                  <div
                    key={`${plan.item.id}-${category.category}`}
                    className='overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]'
                  >
                    <div className='border-b border-white/[0.06] bg-white/[0.03] px-3 py-2.5'>
                      <p className='text-xs font-semibold text-text-primary'>{category.category}</p>
                      <p className='text-[10px] text-text-muted'>
                        {category.services.length}{' '}
                        {category.services.length === 1 ? 'item incluído' : 'itens incluídos'}
                      </p>
                    </div>
                    <ul className='divide-y divide-white/[0.04]'>
                      {category.services.map((service) => (
                        <li
                          key={`${category.category}-${service.name}`}
                          className='flex items-start gap-2 px-3 py-2'
                        >
                          <span
                            className='mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full'
                            style={{
                              backgroundColor: `${plan.tipoColor}20`,
                              color: plan.tipoColor,
                            }}
                          >
                            <Check size={10} strokeWidth={3} />
                          </span>
                          <div className='min-w-0 flex-1'>
                            <p className='text-[13px] leading-snug text-text-primary'>
                              {service.name}
                              {service.isPartial && (
                                <span className='ml-1 text-[10px] font-medium text-amber-400/90'>
                                  (parcial)
                                </span>
                              )}
                            </p>
                            {service.quantity && service.quantity !== '1' && (
                              <p className='mt-0.5 text-[10px] text-text-muted'>
                                Qtd: {service.quantity}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

type RowProps = {
  descricao: string
  tipo: 'plano' | 'servico' | 'avulso'
  quantidade: number
  total?: number
  scopeCount: number
  canViewFinancialValues: boolean
  onOpen: () => void
}

export function FinanceClosedScopeLineRow({
  descricao,
  tipo,
  quantidade,
  total,
  scopeCount,
  canViewFinancialValues,
  onOpen,
}: RowProps) {
  return (
    <li>
      <button
        type='button'
        onClick={onOpen}
        className={cn(
          'flex w-full flex-wrap items-start justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3 text-left',
          'transition-colors hover:border-accent/35 hover:bg-white/[0.05]',
        )}
      >
        <div className='min-w-0 flex-1'>
          <p className='font-semibold text-text-primary'>{descricao}</p>
          <div className='mt-1 flex flex-wrap items-center gap-2 text-xs text-text-muted'>
            <span
              className={cn(
                'rounded-md px-2 py-0.5 font-semibold uppercase tracking-wide',
                tipo === 'plano' && 'bg-indigo-500/15 text-indigo-300',
                tipo === 'servico' && 'bg-emerald-500/15 text-emerald-300',
                tipo === 'avulso' && 'bg-amber-500/15 text-amber-300',
              )}
            >
              {closedScopeTipoLabel(tipo)}
            </span>
            <span>Qtd. {quantidade}</span>
            {scopeCount > 0 && (
              <span>
                {scopeCount} {scopeCount === 1 ? 'entregável' : 'entregáveis'}
              </span>
            )}
          </div>
          <p className='mt-1.5 text-[11px] font-medium text-accent'>Ver escopo detalhado</p>
        </div>
        <div className='flex shrink-0 items-center gap-2'>
          {canViewFinancialValues && total != null && total > 0 && (
            <p className='text-sm font-bold tabular-nums text-accent'>{formatCurrency(total)}</p>
          )}
          <ChevronRight size={16} className='text-text-muted' aria-hidden />
        </div>
      </button>
    </li>
  )
}
